import { DOCUMENT } from '@angular/common';
import {
  DestroyRef,
  inject,
  InputSignal,
  isSignal,
  Service,
  Type,
  WritableSignal,
} from '@angular/core';
import {
  NgbModal,
  NgbModalOptions,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { AnyResource, ResourceType } from '@models';
import { onSessionEnd } from '@store';
import { Community } from '../models/community';

const DISMISSAL_GRACE_MS = 1000;

type ModalInputValue<V> =
  V extends InputSignal<infer U>
    ? U
    : V extends WritableSignal<infer U>
      ? U
      : V;

export type ModalData<T> = { [K in keyof T]?: ModalInputValue<T[K]> };

type ConfirmationWindowParams = {
  title: string;
  text: string;
  request?: {
    url: string;
    method: 'put' | 'post' | 'delete';
    body?: Record<string, unknown>;
  };
  action?: () => Observable<unknown>;
  result?: unknown;
  confirmLabel?: string;
  icon?: string;
  successText?: string;
  infoText?: string;
  infoGroupName?: string;
  danger?: boolean;
  shortNamesMessage?: boolean;
};

@Service()
export class ModalService {
  ngbModalService = inject(NgbModal);
  translate = inject(TranslateService);
  document = inject(DOCUMENT);
  destroyRef = inject(DestroyRef);

  openedModals: NgbModalRef[] = [];
  pendingDismissals = new Map<unknown, number>();

  constructor() {
    onSessionEnd(() => this.ngbModalService.dismissAll());

    const view = this.document.defaultView;

    if (!view) {
      return;
    }

    const silenceDismissals = (event: PromiseRejectionEvent) => {
      if (this.pendingDismissals.has(event.reason)) {
        event.preventDefault();
      }
    };

    view.addEventListener('unhandledrejection', silenceDismissals);
    this.destroyRef.onDestroy(() =>
      view.removeEventListener('unhandledrejection', silenceDismissals)
    );
  }

  getModalRef = <T>(
    component: Type<T>,
    data?: ModalData<T>,
    options?: NgbModalOptions
  ) => {
    const modalRef = this.ngbModalService.open(component, {
      backdrop: 'static',
      ...options,
    });

    this.assignInputs(
      modalRef.componentInstance,
      data as Record<string, unknown>
    );

    return modalRef;
  };

  assignInputs = (instance: unknown, data?: Record<string, unknown>) => {
    if (!data) {
      return;
    }

    const target = instance as Record<string, unknown>;

    Object.entries(data).forEach(([key, value]) => {
      const current = target[key];

      if (
        isSignal(current) &&
        typeof (current as WritableSignal<unknown>).set === 'function'
      ) {
        (current as WritableSignal<unknown>).set(value);
      } else {
        target[key] = value;
      }
    });
  };

  open = <T>(
    component: Type<T>,
    data?: ModalData<T>,
    options?: NgbModalOptions
  ) => {
    const modalRef = this.getModalRef(component, data, options);

    this.openedModals = [...this.openedModals, modalRef];

    modalRef.result
      .finally(() => {
        this.openedModals = this.openedModals.filter((m) => m !== modalRef);
      })
      .catch((reason: unknown) => this.trackDismissal(reason));

    return modalRef.result;
  };

  trackDismissal = (reason: unknown) => {
    this.pendingDismissals.set(
      reason,
      (this.pendingDismissals.get(reason) ?? 0) + 1
    );

    setTimeout(() => {
      const remaining = (this.pendingDismissals.get(reason) ?? 1) - 1;

      if (remaining > 0) {
        this.pendingDismissals.set(reason, remaining);
      } else {
        this.pendingDismissals.delete(reason);
      }
    }, DISMISSAL_GRACE_MS);
  };

  openConfirmation = async (params: ConfirmationWindowParams) => {
    const { ConfirmationModalComponent } =
      await import('@shared/components/modal/confirmation-modal.component');

    return this.open(ConfirmationModalComponent, params, {
      modalDialogClass: 'sm-modal',
    });
  };

  closeAll = () => {
    this.openedModals.filter((modal) => {
      modal.close();

      return false;
    });
  };

  deleteCommunity = async (community: Community) => {
    const { DeleteCommunityModalComponent } =
      await import('../../shared/components/delete-community-modal.component');

    return this.open(DeleteCommunityModalComponent, { community });
  };

  generateCommunityCEC = () =>
    this.openConfirmation({
      title: 'WEB_DIALOGS_COMMUNITY_CEC_GENERATE',
      text: 'WEB_DIALOGS_CONFIRM',
      successText: 'WEB_NOTIFY_COMMUNITY_CEC_SUCCESS',
    });

  downloadRoom = (resource: Nullable<AnyResource | AnyResource[]>) => {
    let single = true;
    let isRoom = false;

    if (resource !== null) {
      if (Array.isArray(resource)) {
        if (resource.length > 1) {
          single = false;
        } else if (resource.length === 1) {
          single = true;
          isRoom = resource[0]?.type === ResourceType.ROOM;
        }
      } else {
        single = true;
        isRoom = resource.type === ResourceType.ROOM;
      }
    }

    let title: string;

    if (single) {
      title = isRoom ? 'WEB_DIALOGS_ROOM_DOWNLOAD' : 'WEB_ACTIONS_DOWNLOAD';
    } else {
      title = 'WEB_DIALOGS_ROOM_ZIP_DOWNLOAD';
    }

    let infoText = '';

    if (!single && Array.isArray(resource)) {
      if (resource.length <= 2) {
        infoText = this.translate.instant(
          `WEB_LIST_SELECTED_ITEMS_${resource.length}`,
          { value: resource.length }
        );
      } else if (resource.length > 2) {
        infoText = this.translate.instant('WEB_LIST_SELECTED_ITEMS_N', {
          value: resource.length,
        });
      }
    }

    return this.openConfirmation({
      title,
      text: 'WEB_DIALOGS_CONFIRM',
      infoText,
      shortNamesMessage: true,
    });
  };
}
